import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import type { GallerySortBy } from 'modules/ScrAppBookNew/helpers/galleryImages';

export type { GallerySortBy };

export interface IGalleryFilterValues {
  sortBy: GallerySortBy;
  sortOrder: 'asc' | 'desc';
  albumIds: string[];
  selectionId: string | null;
  faceIds: string[];
  // Indices into the active set, not glyphs — it can be re-themed server-side.
  reactionSlots: number[];
  combineFilters: boolean;
}

export interface IFiltersState {
  pending: IGalleryFilterValues;
  applied: IGalleryFilterValues;
  randomSeed: number;
  showOnlySelected: boolean;
  // filter-panel draft of showOnlySelected; committed on apply like `pending`
  pendingShowOnlySelected: boolean;
  panelOpen: boolean;
  // bumped on every apply so re-applying the same filters still triggers the scroll
  appliedNonce: number;
}

export const initialFilterValues: IGalleryFilterValues = {
  sortBy: 'default',
  sortOrder: 'asc',
  albumIds: [],
  selectionId: null,
  faceIds: [],
  reactionSlots: [],
  combineFilters: false
};

const initialState: IFiltersState = {
  pending: initialFilterValues,
  applied: initialFilterValues,
  randomSeed: 1,
  showOnlySelected: false,
  pendingShowOnlySelected: false,
  panelOpen: false,
  appliedNonce: 0
};

const filtersSlice = createSlice({
  name: 'scrAppBookNew/filters',
  initialState,
  reducers: {
    setPendingFilters: (state, action: PayloadAction<Partial<IGalleryFilterValues>>) => {
      state.pending = { ...state.pending, ...action.payload };
    },
    applyPendingFilters: (state) => {
      state.applied = state.pending;
      state.showOnlySelected = state.pendingShowOnlySelected;
      state.appliedNonce = (state.appliedNonce || 0) + 1;
      // re-applying "random" must reshuffle; bumping randomSeed busts the sort memo
      if (state.pending.sortBy === 'random') {
        state.randomSeed = state.appliedNonce;
      }
    },
    revertPendingFilters: (state) => {
      state.pending = state.applied;
      state.pendingShowOnlySelected = state.showOnlySelected;
    },
    resetFilters: (state) => {
      state.pending = initialFilterValues;
      state.applied = initialFilterValues;
      state.showOnlySelected = false;
      state.pendingShowOnlySelected = false;
    },
    clearSelectionFilter: (state) => {
      state.pending.selectionId = null;
      state.applied.selectionId = null;
    },
    hydrateFilters: (state, action: PayloadAction<{ values: IGalleryFilterValues }>) => {
      state.pending = action.payload.values;
      state.applied = action.payload.values;
    },
    toggleShowOnlySelected: (state) => {
      state.showOnlySelected = !state.showOnlySelected;
      state.pendingShowOnlySelected = state.showOnlySelected;
    },
    hideSelectedResults: (state) => {
      state.showOnlySelected = false;
      state.pendingShowOnlySelected = false;
    },
    setPendingShowOnlySelected: (state, action: PayloadAction<boolean>) => {
      state.pendingShowOnlySelected = action.payload;
    },
    setFilterPanelOpen: (state, action: PayloadAction<boolean>) => {
      state.panelOpen = action.payload;
    }
  }
});

export const {
  setPendingFilters,
  applyPendingFilters,
  revertPendingFilters,
  resetFilters,
  clearSelectionFilter,
  hydrateFilters,
  toggleShowOnlySelected,
  hideSelectedResults,
  setPendingShowOnlySelected,
  setFilterPanelOpen
} = filtersSlice.actions;

export const filtersReducer = filtersSlice.reducer;
